import React, { FC, ReactNode, useMemo, useContext, useEffect, useCallback, useRef } from 'react';
import { getOmniLogger } from 'omni-package-react-logging';
import { useNavigate, useLocation, Location } from 'react-router-dom';
import { AuthenticationContext, redirectTo } from 'omni-package-react-core';
import Constants from '~/app/Constants';
import getTokenManager from '~/utils/getTokenManager';
import { closeCurrentSession } from 'aw-package-react-authentication';
import { isAuthorizeCallbackPath, getExternalLogoffUrl } from '~/utils/companyUtils';
import { setLocationHref, replaceLocation } from '~/utils/browserNavigation';
import { Company } from '~/interfaces/CompanyConfig';
import AppLoadingIcon from '../AppLoadingIcon';

export interface OpenIdConnectorWrapperProps {
  children: ReactNode;
}

const OpenIdConnectorWrapper: FC<OpenIdConnectorWrapperProps> = ({ children }) => {
  const authCtx = useContext(AuthenticationContext);
  const location = useLocation();
  const { state } = location as Location<{ data?: { finalPath?: string; company?: Company } }>;
  const navigate = useNavigate();
  const logger = getOmniLogger('we-client-space-oidc-wrapper');
  const tokenManager = useMemo(() => getTokenManager(), []);
  // Regression note (branch vs master baseline): the authorize callback can be entered twice
  // during quick remount/navigation transitions, which re-triggers bootstrap resource flows
  // (including version-related startup calls) and may end in a bad fallback redirect.
  // We keep a per-callback key + in-progress guard to make callback auth idempotent.
  const processedAuthorizeCallbackRef = useRef<string | undefined>(undefined);
  const isAuthenticatingAuthorizeCallbackRef = useRef<boolean>(false);

  const isProtectedRoute = useMemo(() => (
    Constants.ProtectedRoutes.includes(location.pathname)
  ), [location.pathname]);

  const isPublicRouteWithAuthLayout = useMemo(() => (
    Constants.PublicRoutesWithAuthenticatedLayout.includes(location.pathname)
  ), [location.pathname]);

  const isAuthorizeCallback = useMemo(() => isAuthorizeCallbackPath(location.pathname), [location.pathname]);
  const isLogOffPage = location.pathname === Constants.PagesUrl.LogOff;
  const isSecureLogOffPage = location.pathname === Constants.PagesUrl.SecureLogOff;
  const isTest = process.env.REACT_APP_REDIRECT_FEDERATED_PORTAL === 'false';

  const handleLogOff = useCallback(async () => {
    try {
      await tokenManager.logOff();
      await closeCurrentSession({ backgroundLogOff: Boolean(state?.data?.finalPath) });
      authCtx.saveAuthenticationInfo({ isAuthenticated: false, isAuthenticationProcessed: false });

      if (state?.data?.finalPath) {
        replaceLocation(state?.data?.finalPath);
      } else if (state?.data?.company) {
        const externalLogoffUrl = getExternalLogoffUrl(state.data.company);
        if (externalLogoffUrl) {
          setLocationHref(externalLogoffUrl);
        }
      }
    } catch (error: unknown) {
      logger.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.data?.finalPath, state?.data?.company]);

  useEffect(() => {
    if (isAuthorizeCallback) {
      const authorizeCallbackKey = `${location.pathname}${location.search}`;
      // Prevent duplicate callback processing for the same OIDC return URL.
      if (processedAuthorizeCallbackRef.current === authorizeCallbackKey || isAuthenticatingAuthorizeCallbackRef.current) {
        return;
      }

      isAuthenticatingAuthorizeCallbackRef.current = true;
      const redirectToUrl = sessionStorage.getItem('redirect-url') ?? Constants.PagesUrl.HomePage;
      // Consume it now so a stale value can't leak into an unrelated future auth round-trip.
      sessionStorage.removeItem('redirect-url');
      tokenManager.authenticate(redirectToUrl)
        .then(async () => {
          processedAuthorizeCallbackRef.current = authorizeCallbackKey;
          const firstAuth = !authCtx.authenticationInfo.isAuthenticated;
          authCtx.saveAuthenticationInfo({ isAuthenticated: true, isAuthenticationProcessed: true });
          const glParams = new URLSearchParams(redirectToUrl.includes('?') ? redirectToUrl.split('?')[1] : '');
          glParams.delete('_gl');
          const cleanSearch = glParams.toString();
          const cleanRedirectToUrl = `${redirectToUrl.split('?')[0]}${cleanSearch ? `?${cleanSearch}` : ''}`;
          if (firstAuth && cleanRedirectToUrl === Constants.PagesUrl.HomePage && !isTest) {
            const postAuthRedirection = await tokenManager.getClaim('post_auth_redirection');
            if (postAuthRedirection !== null && postAuthRedirection !== undefined && postAuthRedirection.trim() !== '') {
              setLocationHref(`${postAuthRedirection}?fromCie=ia`);
            } else {
              redirectTo(redirectToUrl, navigate);
            }
          } else {
            redirectTo(redirectToUrl, navigate);
          }
        }).catch((error: unknown) => {
          logger.error(error);
          redirectTo(Constants.PagesUrl.HomePage, navigate);
        }).finally(() => {
          isAuthenticatingAuthorizeCallbackRef.current = false;
        });
    } else if ((isProtectedRoute && !authCtx.authenticationInfo.isAuthenticationProcessed)
      || (isPublicRouteWithAuthLayout && authCtx.authenticationInfo.isAuthenticated && !authCtx.authenticationInfo.isAuthenticationProcessed)) {
      const redirectToUrl = location.pathname + location.search;
      if (redirectToUrl !== '/') {
        sessionStorage.setItem('redirect-url', redirectToUrl);
      }
      tokenManager.init(redirectToUrl)
        .then((url) => {
          setLocationHref(url);
        })
        .catch((err: unknown) => {
          logger.error(err);
          redirectTo(Constants.PagesUrl.TechnicalErrorPage, navigate);
        });
    }
  }, [authCtx, authCtx.authenticationInfo.isAuthenticationProcessed,
    authCtx.authenticationInfo.isAuthenticated, isAuthorizeCallback,
    isProtectedRoute, location.pathname, location.search, navigate,
    tokenManager, isPublicRouteWithAuthLayout, isTest, logger]);

  useEffect(() => {
    if (!isAuthorizeCallback) {
      isAuthenticatingAuthorizeCallbackRef.current = false;
    }
  }, [isAuthorizeCallback]);

  useEffect(() => {
    if (isLogOffPage) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handleLogOff();
    }
  }, [handleLogOff, isLogOffPage]);

  if (isAuthorizeCallback || isLogOffPage || isSecureLogOffPage) {
    return <AppLoadingIcon />;
  }

  if (!isProtectedRoute || authCtx.authenticationInfo.isAuthenticationProcessed) {
    return (<>{children}</>);
  }

  return null;
};

export default OpenIdConnectorWrapper;
